import {sendEvent} from '@github-ui/hydro-analytics'
import {SidebarListItem} from '@github-ui/pacer/SidebarListItem'
import {useCallback, useRef, useState} from 'react'
import type React from 'react'

import {useResolvedTaskTitle} from '@github-ui/copilot-shared-ui/hooks/use-task-title-override'
import {resolveAgentProfileForTask} from '../../agents/resolve-agent-profile'
import {isQuickChatTask} from '../../utils/is-quick-chat-task'
import type {UITask} from '../../types/task'
import {getTaskTitle} from '../../utils/task-helpers'
import {getLeadingVisualIconAttributes} from '../../utils/get-leading-visual-icon-attributes'
import {getEffectivePrState} from '../../utils/task-artifact-helpers'
import {TaskActions} from '../TaskActions'
import {RenameTaskDialog} from '../RenameTaskDialog'

export interface SidebarTaskListItemProps {
  task: UITask
  href: string
  isSelected: boolean
  secondaryText: string | React.ReactNode
  reloadDocument: boolean
  onTaskClick?: () => void
  repoOwner?: string
  repoName?: string
  currentUserIsOwner?: boolean
  preferPullTitle?: boolean
  formatAsReview?: boolean
  dataUpdatedAt?: number
}

export function SidebarTaskListItem({
  task,
  href,
  isSelected,
  secondaryText,
  reloadDocument,
  onTaskClick,
  repoOwner,
  repoName,
  currentUserIsOwner,
  preferPullTitle,
  formatAsReview,
  dataUpdatedAt,
}: SidebarTaskListItemProps) {
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [isRenaming, setIsRenaming] = useState(false)

  const sessionState = task.latest_session_state
  const isOptimistic = !!task.isOptimistic
  const prInMergeQueue = task.pull?.inMergeQueue
  const isArchived = !!task.archived_at
  const agentProfile = resolveAgentProfileForTask(task)
  const TrailingVisual = isQuickChatTask(task) ? agentProfile.slots.TrailingVisual : null

  const taskTitle = useResolvedTaskTitle(
    task.id,
    getTaskTitle(task, task.pull?.title, {preferPullTitle, formatAsReview}),
  )

  const handleStartRename = useCallback(() => {
    setIsRenaming(true)
    sendEvent('agent_sessions.task_actions', {
      target: 'RENAME_TASK_START',
      task_id: task.id,
    })
  }, [task.id])

  const handleKebabOpen = useCallback(() => {
    sendEvent('dotcom_chat.activate', {
      target: 'TASK_CONTEXT_MENU',
      mode: 'immersive',
    })
  }, [])

  const trailingAction = (
    <TaskActions
      ariaLabel="Manage session"
      anchorRef={anchorRef}
      onOpen={handleKebabOpen}
      state={task.latest_session_state}
      pull={task.pull}
      taskId={task.id}
      repoOwner={repoOwner}
      repoName={repoName}
      currentUserIsOwner={currentUserIsOwner}
      archivedAt={task.archived_at}
      eventType={task.event_type}
      eventIdentifiers={task.event_identifiers}
      interactiveSession={agentProfile.interactiveSession}
      agentSlug={agentProfile.slug}
      isQuickChatTask={isQuickChatTask(task)}
      hideCopy={agentProfile.privateSessions}
      hideShare={!agentProfile.privateSessions}
      onRename={handleStartRename}
      sharingStatus={task.sharing_status}
    />
  )

  const effectivePrStatus = getEffectivePrState(task.artifact_state, task.pull)

  const leadingVisual = useCallback(() => {
    const {
      icon: Icon,
      color,
      'data-testid': testId,
      marginTop,
    } = getLeadingVisualIconAttributes(
      effectivePrStatus,
      prInMergeQueue,
      isOptimistic ? 'in_progress' : sessionState,
      isArchived,
      task.agentSlug,
    )
    return (
      <span style={{color, display: 'inline-flex', marginTop}} data-testid={testId}>
        <Icon />
      </span>
    )
  }, [prInMergeQueue, isArchived, isOptimistic, sessionState, effectivePrStatus, task.agentSlug])

  return (
    <>
      <SidebarListItem
        href={href}
        text={taskTitle}
        leadingVisual={leadingVisual}
        isSelected={isSelected}
        trailingAction={trailingAction}
        crossfadeTrailing
        trailingVisual={
          TrailingVisual ? (
            <TrailingVisual
              state={sessionState}
              staleAt={task.stale_at}
              dataUpdatedAt={dataUpdatedAt}
              sharingStatus={task.sharing_status}
            />
          ) : undefined
        }
        secondaryText={secondaryText}
        reloadDocument={reloadDocument}
        onClick={() => {
          onTaskClick?.()
          sendEvent('agent_sessions.task_view', {
            target: 'VIEW_TASK_FROM_MISSION_CONTROL',
            task_id: task.resource_global_id,
          })
        }}
      />
      {isRenaming && (
        <RenameTaskDialog
          currentName={task.name || taskTitle}
          taskId={task.id}
          repoOwner={repoOwner}
          repoName={repoName}
          onClose={() => setIsRenaming(false)}
          returnFocusRef={anchorRef}
        />
      )}
    </>
  )
}
